import { useState } from 'react';
import { Stack, Typography } from '@mui/material';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { Button } from 'framework/ui/mui-builder';
import { ErrorMessage, SuccessMessage, useMessageService } from 'framework/ui/Message';
import { useShowContext } from 'framework/show';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useProjectDataContext,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import useReconcileProjectNotifications, {
  PROJECT_NOTIFICATION_SCOPE,
} from '../../shared/notifications/useReconcileProjectNotifications';
import useEngineeringService from '../useEngineeringService';
import { ProjectEngineeringGraphqlData } from '../types';
import { PPC_LINK_NOT_CONFIRMED_MESSAGE } from '../constants';
import { useDateService } from 'framework/utils';

const PPC_LINK_CONFIRMATION_DAYS = 30;

const PpcLinkConfirmBanner = () => {
  const { data: showData, setData } = useShowContext<ProjectEngineeringGraphqlData>();
  const { isAccessGranted } = useProjectSecurityContext();
  const { data: projectData } = useProjectDataContext();
  const { confirmPpcLink } = useEngineeringService();
  const reconcileNotifications = useReconcileProjectNotifications();
  const { addMessage } = useMessageService();
  const { getMomentDateObj, getToday } = useDateService();
  const [isConfirming, setIsConfirming] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const engineeringData = showData.getData();
  const ppcLink = engineeringData?.engineeringPolicy?.ppcLink;
  const ppcLinkUpdatedAt = engineeringData?.engineeringPolicy?.ppcLinkUpdatedAt;
  const ppcLinkUpdatedAtDate = getMomentDateObj(ppcLinkUpdatedAt);
  const canManageEngineering = isAccessGranted(ACCESS_INFO_SECTIONS.ENGINEERING, ACCESS_INFO_ACTIONS.MANAGE);
  const isPpcLinkConfirmationExpired =
    ppcLinkUpdatedAtDate.isValid() &&
    ppcLinkUpdatedAtDate.isBefore(getToday().clone().subtract(PPC_LINK_CONFIRMATION_DAYS, 'days'));

  const canConfirmPpcLink = !isConfirmed && canManageEngineering && !!ppcLink && isPpcLinkConfirmationExpired;

  const handleConfirmPpcLink = async () => {
    const projectId = projectData?.id?.toString() ?? engineeringData?.id;
    if (!projectId) return;
    setIsConfirming(true);
    try {
      const updatedPolicy = await confirmPpcLink(projectId);
      await reconcileNotifications(projectId, [PROJECT_NOTIFICATION_SCOPE.ENGINEERING]).catch(() => undefined);
      setIsConfirmed(true);
      if (engineeringData) {
        setData?.({
          ...engineeringData,
          engineeringPolicy: { ...engineeringData.engineeringPolicy, ...updatedPolicy },
        });
      }
      addMessage(new SuccessMessage('PPC link has been confirmed.'));
    } catch {
      addMessage(new ErrorMessage('Failed to confirm PPC link. Please try again.'));
    } finally {
      setIsConfirming(false);
    }
  };

  if (!canConfirmPpcLink) {
    return null;
  }

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1 }}>
        <WarningAmberOutlined color="error" fontSize="small" />
        <Typography variant="body1">{PPC_LINK_NOT_CONFIRMED_MESSAGE}</Typography>
      </Stack>
      <Button variant="contained" onClick={handleConfirmPpcLink} loading={isConfirming}>
        Confirm
      </Button>
    </Stack>
  );
};

export default PpcLinkConfirmBanner;
